import React from "react";
import { Link } from "react-router-dom";

/**
 * L'état ERREUR d'une liste — le pendant de <EmptyState>, et sa contrepartie
 * indispensable.
 *
 * Règle qui a motivé ce composant (audit UX du 20/08/2026) : **une panne n'est
 * pas une absence**. Un écran ne doit JAMAIS écrire « Aucun don », « Aucune
 * annonce » ou « Lignes 0 » quand il ne sait pas — il doit dire qu'il n'a pas
 * pu charger, rassurer sur les données, et proposer une action.
 *
 * Deux principes de rédaction, tenus dans tous les textes ci-dessous :
 *  - on affirme toujours que **rien n'a été perdu** (c'est la peur réelle de
 *    l'utilisateur devant un écran vide) ;
 *  - un état d'erreur ne propose JAMAIS une action de création (« Publier une
 *    annonce », « Parcourir les annonces ») : proposer de créer à quelqu'un
 *    dont les données existent déjà mais n'ont pas pu être lues, c'est
 *    l'inviter à produire un doublon (cf. DUPLICATE_GUARDRAILS.md).
 *
 * `kind` provient de `classifyApiError()` et distingue quatre situations que
 * l'audit a trouvées confondues partout. La session expirée notamment n'appelle
 * pas « Réessayer » mais « Se reconnecter » : réessayer n'y changerait rien.
 */

const COPY = {
  network: {
    title: "Impossible d'afficher cette liste",
    text: "Votre appareil n'arrive pas à joindre Gively. Vos données sont bien enregistrées — rien n'a été perdu. Vérifiez votre connexion Internet, puis réessayez.",
  },
  timeout: {
    title: "Le chargement prend trop de temps",
    text: "Gively met plus de temps que prévu à répondre. Vos données sont intactes.",
  },
  server: {
    title: "Gively rencontre un problème technique",
    text: "Ce n'est pas de votre fait, et rien n'a été perdu : nos équipes sont prévenues automatiquement. Réessayez dans quelques instants.",
  },
  ratelimit: {
    title: "Trop de demandes d'affilée",
    text: "Patientez une minute, puis réessayez.",
  },
  auth: {
    title: "Vous avez été déconnecté",
    text: "Pour votre sécurité, votre connexion a expiré. Vos données sont intactes — reconnectez-vous pour les retrouver.",
  },
  unknown: {
    title: "Impossible d'afficher cette liste",
    text: "Une erreur est survenue pendant le chargement. Vos données sont bien enregistrées — rien n'a été perdu.",
  },
};

/**
 * @param {string} [titre] — remplace le titre quand « cette liste » serait faux
 *   (une fiche n'est pas une liste : fiche association, 11/10/2026).
 * @param {string} [texte] — remplace le texte quand « vos données sont bien
 *   enregistrées » n'a pas de sens (un visiteur d'une page publique n'a rien
 *   enregistré). Par défaut, le texte du `kind`.
 * @param {string} [loginTo] — destination de « Se reconnecter ». L'espace
 *   ASSOCIATION a son propre tunnel d'authentification (`/association/login`) :
 *   y envoyer une association vers `/login` la ferait atterrir sur le formulaire
 *   des particuliers, où ses identifiants ne fonctionnent pas. Un écran d'erreur
 *   qui propose la mauvaise porte est à peine mieux qu'un écran muet.
 */
export default function ErrorState({ kind = "unknown", onRetry, testId, compact = false, loginTo = "/login", titre, texte }) {
  const copy = COPY[kind] || COPY.unknown;
  const isAuth = kind === "auth";

  return (
    <div
      className={`text-center px-6 ${compact ? "py-6" : "py-12"}`}
      data-testid={testId || "list-error"}
      role="status"
    >
      <div className="mx-auto mb-4 w-14 h-14 rounded-2xl bg-amber-50 border border-amber-200 flex items-center justify-center text-amber-700">
        {/* Ambre = « il manque quelque chose », jamais rouge : ce n'est pas une
            faute de l'utilisateur, et le rouge dramatise une panne réseau. */}
        <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" strokeWidth={1.8} stroke="currentColor" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
        </svg>
      </div>

      <p className="font-semibold text-slate-900">{titre || copy.title}</p>
      <p className="text-sm text-slate-600 mt-1 max-w-sm mx-auto">{texte || copy.text}</p>

      {isAuth ? (
        <Link
          to={loginTo}
          data-testid={`${testId || "list-error"}-login`}
          className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 rounded-full text-sm font-semibold bg-[var(--primary-dark)] text-white hover:bg-[var(--primary)] transition-colors"
        >
          Se reconnecter
        </Link>
      ) : onRetry ? (
        <button
          type="button"
          onClick={onRetry}
          data-testid={`${testId || "list-error"}-retry`}
          className="inline-flex items-center gap-1.5 mt-4 px-4 py-2 rounded-full text-sm font-semibold bg-[var(--primary-dark)] text-white hover:bg-[var(--primary)] transition-colors"
        >
          Réessayer
        </button>
      ) : null}
    </div>
  );
}
